import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';

type ResizeHandle = 'e' | 's' | 'se';

@Component({
  selector: 'app-encounter-documents',
  templateUrl: './encounter-documents.component.html',
  styleUrls: ['./encounter-documents.component.css'],
})
export class EncounterDocumentsComponent implements OnChanges {
  @Input() encounterId!: number; // parent-controlled value
  @Input() encounterWhiteLabelDomain!: string; // parent-controlled value

  @Output() closed = new EventEmitter<void>();

  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLElement>;

  // Window geometry (px)
  width = window.innerWidth / 2;
  height = window.innerHeight / 1.5;
  x = ( window.innerWidth - this.width ) / 2;
  y = ( window.innerHeight - this.height ) / 2;

  // Constraints
  minWidth = 280;
  minHeight = 180;

  iframeUrl!: SafeResourceUrl;

  private dragActive = false;
  private resizeActive: ResizeHandle | null = null;

  private startPointerX = 0;
  private startPointerY = 0;

  private startX = 0;
  private startY = 0;
  private startW = 0;
  private startH = 0;

  constructor(private sanitizer: DomSanitizer) {
    this.sanitizer = sanitizer;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['encounterId'] && !changes['encounterId'].firstChange) {
      this.updateIframeUrl();
    }
    // If id is set after first render, also handle firstChange:
    if (changes['encounterId'] && changes['encounterId'].firstChange) {
      this.updateIframeUrl();
    }
  }

  close(): void {
    this.closed.emit();
  }

  onHeaderPointerDown(ev: PointerEvent): void {
    // avoid starting drag when user clicks buttons
    const target = ev.target as HTMLElement;
    if (target.closest('button')) return;

    this.dragActive = true;
    this.capturePointer(ev);

    this.startPointerX = ev.clientX;
    this.startPointerY = ev.clientY;

    this.startX = this.x;
    this.startY = this.y;
  }

  onResizePointerDown(ev: PointerEvent, handle: ResizeHandle): void {
    this.resizeActive = handle;
    this.capturePointer(ev);

    this.startPointerX = ev.clientX;
    this.startPointerY = ev.clientY;

    this.startX = this.x;
    this.startY = this.y;
    this.startW = this.width;
    this.startH = this.height;
  }

  @HostListener('window:pointermove', ['$event'])
  onPointerMove(ev: PointerEvent): void {

    const dx = ev.clientX - this.startPointerX;
    const dy = ev.clientY - this.startPointerY;

    if (this.dragActive) {
      this.x = this.startX + dx;
      this.y = this.startY + dy;
      this.clampToViewport();
      return;
    }

    if (this.resizeActive) {
      let newW = this.startW;
      let newH = this.startH;

      if (this.resizeActive === 'e' || this.resizeActive === 'se') {
        newW = this.startW + dx;
      }
      if (this.resizeActive === 's' || this.resizeActive === 'se') {
        newH = this.startH + dy;
      }

      this.width = Math.max(this.minWidth, newW);
      this.height = Math.max(this.minHeight, newH);

      this.clampToViewport();
    }
  }

  @HostListener('window:pointerup', ['$event'])
  onPointerUp(_ev: PointerEvent): void {
    this.dragActive = false;
    this.resizeActive = null;
  }

  private updateIframeUrl(): void {
    if ( this.encounterId == null ) return;
        if ( this.encounterWhiteLabelDomain == null ) {
      this.encounterWhiteLabelDomain = "harlowehealth";
    } else {
      this.encounterWhiteLabelDomain = this.encounterWhiteLabelDomain.split('.')[0];
    }
    const raw = `${environment.urlCorrespondenceManager}/${encodeURIComponent(String(this.encounterId ?? ''))}?showMenu=false&domain=${encodeURIComponent(this.encounterWhiteLabelDomain)}`;
    // Angular requires SafeResourceUrl for [src] on iframe.
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(raw);
  }

  private clampToViewport(): void {
    // Keep the window at least partially on-screen
    const padding = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const maxX = Math.max(padding, vw - padding);
    const maxY = Math.max(padding, vh - padding);

    this.x = Math.min(Math.max(this.x, padding - this.width + 60), maxX - 60);
    this.y = Math.min(Math.max(this.y, padding), maxY - 40);

    // Also ensure size doesn't exceed viewport too much
    this.width = Math.min(this.width, vw - padding * 2);
    this.height = Math.min(this.height, vh - padding * 2);
  }

  private capturePointer(ev: PointerEvent): void {
    try {
      (ev.currentTarget as HTMLElement)?.setPointerCapture?.(ev.pointerId);
    } catch {
      // safe to ignore
    }
    ev.preventDefault();
  }
}
